import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from '@angular/material/dialog';

import { ProjectService } from "../shared/project/project.service";
import { DataService } from "../shared/network/data.service";
import { NotificationService } from "../shared/notificaton.service";
import { CardHelper } from "./cardHelper";
import { BaseCardsComponent } from "./base-cards-component";
import { FcstWalkTab, MainFcstWalkModel, FcstWalkTreeNode } from "../shared/project/cards/fcst-walk-model";
import { DriverType } from "../shared/project/driver-types/driver-type";
import { Card, FcstWalkCardType } from "../shared/project/cards/card";
import { CardsLoadingService } from "../shared/project/cards/cards-loading.service";
import { ViewsModel } from "../shared/project/cards/views-model";
import { CardsModel } from "../shared/project/cards/cards-model";
import { DimensionLockType } from "../shared/project/cards/dimension-lock-type";
import { LegendItem } from "../shared/project/cards/legend-item";
import { BenchmarksModel } from "../shared/benchmarks/benchmarks-model";
import { TrendModalComponent } from "./trend/trend-modal.component";

@Component({
    templateUrl: './project-overview.component.html',
    styleUrls: ['./cards.component.scss']
})
export class ProjectOverviewComponent extends BaseCardsComponent implements OnInit {
    DriverType: typeof DriverType = DriverType;   //hack for enum to be available in the template
    FcstWalkCardType: typeof FcstWalkCardType = FcstWalkCardType;
    DimensionLockType: typeof DimensionLockType = DimensionLockType;
    public showSideBar = true;
    public showFcst: boolean = false;
    public projectId: string;
    viewsModel: ViewsModel;
    mainFcstWalkModel: MainFcstWalkModel;
    loading: boolean = true;
    selectedTabIx: number = undefined;
    xlsxDownloadLink: string;
    dwpDownloadLink: string;
    legend: LegendItem[][];
    public selectedGuiTabIndex: number = 0;

    story: string;
    savedStory: string;
    loadingStory: boolean = false;

    quickViewDeltasCtx = {
        roundTo: 1,
        showPlusForPositive: true,
        cap: undefined,
        scale: undefined,
    };
    private rootBenchmarksModel: BenchmarksModel | undefined;

    private static readonly TREND_TAB_INDEX = 3;   // Walk, Story, Summary, Trend

    //mini trend diagram on the overview card, same setup as the cards screen
    public chartOptions = CardHelper.createChartOptions();
    public viewBoxWidth = 100;
    public viewBoxHeight = 40;

    constructor(private activatedRoute: ActivatedRoute, private router: Router,
        private dataService: DataService, private notifier: NotificationService,
        private cardsLoadingService: CardsLoadingService, private projectService: ProjectService,
        public dialog: MatDialog) {
        super();
        this.quillModules = this.createQuillModules(false);    //no AI story regeneration on the project overview
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            let fcstWalk = queryParams.fcstWalk;
            if (projectId) {
                this.projectId = projectId;
                this.dwpDownloadLink = `${this.dataService.getBaseUrl()}/projects/export/${projectId}`;
                this.showFcst = fcstWalk !== undefined;    //the URL decides the side: a forecast walk deep link (tree nodes, VAR/FOR switch) opens FOR, otherwise VAR
                this.loadDataAsync(projectId, fcstWalk);
            }
        });
    }

    private async loadDataAsync(projectId: string, fcstWalk?: string): Promise<void> {
        try {
            // Check if the service already has the data loaded
            if (this.cardsLoadingService.viewsModel && this.cardsLoadingService.currentProjectId === projectId) {
                this.viewsModel = this.cardsLoadingService.viewsModel;
                this.loadOverview();
                this.mainFcstWalkModel = this.cardsLoadingService.mainFcstWalkModel;
                this.loadModel(this.mainFcstWalkModel, fcstWalk);
                this.loading = false;
                return;
            }

            const viewsRes = await this.cardsLoadingService.loadViews(projectId);
            this.viewsModel = viewsRes;
            this.loadOverview();

            // The forecast walk is optional here: without forecast adjustments the overview still renders
            const mainFcstWalkRes = await this.cardsLoadingService.loadMainFcstWalkModel(projectId);

            this.mainFcstWalkModel = mainFcstWalkRes;
            this.loadModel(mainFcstWalkRes, fcstWalk);
            this.loading = false;
        } catch (error) {
            this.loading = false;
            this.notifier.showError("Error loading project overview");
        }
    }

    /** Project-level part of the ViewsModel: story and legend. */
    private loadOverview() {
        const overview = this.viewsModel?.overview;
        this.savedStory = this.story = overview?.story ?? '';
        const legend = overview?.legend ?? this.viewsModel?.views?.[0]?.legend;
        this.legend = legend ? CardHelper.cloneObject(legend) : [];
        if (overview?.storyIsBeingLoaded && !overview.story) {
            this.loadProjectStory();
        }
    }

    /** The project-level story is generated in the background right after the view stories: long-poll it, as the cards screen does for the tab stories. */
    private loadProjectStory() {
        if (this.loadingStory) {
            return;
        }
        this.loadingStory = true;
        this.pollStory(this.dataService, `processing/getProjectStory/${this.projectId}`).then(story => {
            const overview = this.viewsModel?.overview;
            if (overview) {
                overview.storyIsBeingLoaded = false;
                if (story) {
                    overview.story = story;
                }
            }
            if (story) {
                this.savedStory = this.story = story;
            }
            this.loadingStory = false;
        }).catch(err => {
            this.loadingStory = false;
            console.error('Project story loading error:', err);
        });
    }

    /** VAR side: the single project-level Outcome card. */
    private loadOverviewWalk() {
        this.selectedGuiTabIndex = this.viewsModel?.overview?.selectedGuiTabIndex ?? 0;
        if (this.selectedGuiTabIndex === ProjectOverviewComponent.TREND_TAB_INDEX && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }

        const variance = this.viewsModel?.overview?.variance;
        if (!variance) {
            this.varianceModel = undefined;
            return;
        }
        this.varianceModel = variance;
        this.loadWaterfallItems(variance.oldTotalOutcome, variance.newTotalOutcome, variance.cards, undefined, [], variance.outcomeAfterOutOfScope);
        CardHelper.calcCardsFancyNumContexts(variance.cards);
        for (const card of variance.cards) {
            if (card.altViewSeq?.diagramSeries) {
                CardHelper.initCardDiagram(card, this.viewsModel.overview.selectedDiagramSeriesName, variance.oldControlValue, variance.newControlValue, true, this.viewBoxWidth, this.viewBoxHeight);
            }
        }
        this.calcOutcomeCtxs();
    }

    private loadModel(model: MainFcstWalkModel | null, fcstWalk?: string) {
        this.mainFcstWalkModel = model;

        if (!model) {
            // No forecast adjustments: only the VAR side exists
            this.selectedTabIx = undefined;
            this.showFcst = false;
            this.createXlsxDownLoadLink();
            this.loadOverviewWalk();
            return;
        }
        if (fcstWalk) {
            this.showFcst = true;   //a forecast walk deep link always opens the FOR side
        }
        
        // Initialize the model to rebuild the tabs tree structure
        CardHelper.initMainFcstWalkModel(model);
        
        this.cardsLoadingService.updateMainFcstWalkModel(model);
        
        this.createXlsxDownLoadLink();
        
        // Handle fcstWalk navigation here instead of after loadModel
        if (fcstWalk) {
            this.handleForecastWalkNavigation(fcstWalk);
        } else {
            if (model.selectedTabIx !== null && model.selectedTabIx !== undefined) {
                this.loadTab(model.selectedTabIx);
            } else {
                this.loadMainTab();
            }
        }
        if (!this.showFcst) {
            this.loadOverviewWalk();    //VAR is the default side; the forecast walk above only prepared the FOR side's selection
        }
    }

    private loadMainTab() {
        this.varianceModel = this.mainFcstWalkModel.varianceModel;
        this.loadWaterfallItems(this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome, this.varianceModel.cards, undefined, [], this.varianceModel.outcomeAfterOutOfScope);
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
        this.selectedTabIx = undefined;
        this.selectedGuiTabIndex = this.mainFcstWalkModel.selectedGuiTabIndex !== undefined ? this.mainFcstWalkModel.selectedGuiTabIndex : 0;
        
        if (this.selectedGuiTabIndex === ProjectOverviewComponent.TREND_TAB_INDEX && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    private loadTab(tIx: number) {
        let tab = this.mainFcstWalkModel.tabs[tIx];
        this.varianceModel = tab.varianceModel;
        this.loadWaterfallItems(this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome, this.varianceModel.cards, undefined, [], this.varianceModel.outcomeAfterOutOfScope);
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
        this.selectedTabIx = tIx;
        this.selectedGuiTabIndex = tab.selectedGuiTabIndex !== undefined ? tab.selectedGuiTabIndex : 0;
        
        if (this.selectedGuiTabIndex === ProjectOverviewComponent.TREND_TAB_INDEX && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    async undo() {
        if (this.loading || (this.mainFcstWalkModel && !this.mainFcstWalkModel.undoAvailable)) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<MainFcstWalkModel>('processing/UndoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: true,
            });
            this.loadModel(model, undefined);
            if (history.state.returnTabIx) {
                this.tabClick(history.state.returnTabIx);
            }
        } finally {
            this.loading = false;
        }
    }

    async redo() {
        if (this.loading || (this.mainFcstWalkModel && !this.mainFcstWalkModel.redoAvailable)) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<MainFcstWalkModel>('processing/RedoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: true,
            });
            this.loadModel(model, undefined);
        } finally {
            this.loading = false;
        }
    }

    private isInternalNgRoute(url: string): boolean {
        const route = url.split('?')[0].substring(1);
        return this.router.config.some(r => route === r.path);
    }

    public quillBlur($event) {
        if (this.story != this.savedStory) {
            this.saveStory();
        }
    }

    public quillClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const classes = target.getAttribute('class');
        if (target.tagName === 'A' && target.hasAttribute('href') && classes == 'ql-preview') {
            event.preventDefault();

            const href = target.getAttribute('href');
            const url = decodeURIComponent(href);

            if (this.isInternalNgRoute(url)) {
                this.router.navigateByUrl(CardHelper.createStoryLinkUrlTree(this.router, href, this.projectId));
            } else {
                window.open(url, '_blank');
            }
        }
    }

    /** Manual story edit. The ViewsModel instance is shared through CardsLoadingService, so the cached copy is patched too. */
    private saveStory() {
        const overview = this.viewsModel?.overview;
        if (overview) {
            overview.story = this.story;
        }
        this.savedStory = this.story;
        this.dataService.post('processing/saveProjectStory', {
            projectId: this.projectId,
            story: this.story,
        });
    }

    public async explodeCard(cardIx: number, tabIx: number | undefined) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let path: number[];
            if (tabIx === undefined) {
                path = [ cardIx ];
            } else {
                path = [ cardIx, ...this.mainFcstWalkModel.tabs[tabIx].path];
            }
            let model = await this.dataService.post<MainFcstWalkModel>('processing/ExplodeFcstWalkTab', {
                projectId: this.projectId,
                cardPath: path,
            });
            this.loadModel(model, undefined);
            
            // Navigate to the newly created tab if one was selected
            if (model.selectedTabIx !== null && model.selectedTabIx !== undefined) {
                this.router.navigate([], {
                    relativeTo: this.activatedRoute,
                    queryParams: { fcstWalk: model.selectedTabIx.toString() },
                    queryParamsHandling: 'merge'
                });
            }
        } finally {
            this.loading = false;
        }
    }

    public closeTab(tabIx: number) {
        this.mainFcstWalkModel.tabs.splice(tabIx, 1);
        this.mainFcstWalkModel.redoAvailable = false;
        
        // Update the service with the modified model
        this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
        
        this.dataService.post<MainFcstWalkModel>('processing/CloseFcstWalkTab', {
            projectId: this.projectId,
            tabIx: tabIx,
        });
        if (tabIx == this.mainFcstWalkModel.selectedTabIx) {
            this.loadMainTab();
        }
    }

    public async removeCard(card: Card) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<MainFcstWalkModel>('processing/RemoveFcstCards', {
                projectId: this.projectId,
                cardPaths: [ card.cardPath ],
            });
            this.loadModel(model, undefined);
        } finally {
            this.loading = false;
        }
    }

    public async collapseMult(card: Card) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<MainFcstWalkModel>('Processing/CollapseMults', {
                projectId: this.projectId,
                cardPaths: [ card.cardPath ],
            });
            this.loadModel(model, undefined);
        } finally {
            this.loading = false;
        }
    }

    /** VAR/FOR switch. The side lives in the URL (fcstWalk param) exactly as for the tree links, so the queryParams subscription reloads it. */
    public selectFcst(val: boolean) {
        if (val === this.showFcst) {
            return;
        }
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { fcstWalk: val ? (this.selectedTabIx ?? 'main').toString() : null },    //FOR returns to the last viewed forecast tab
            queryParamsHandling: 'merge',
        });
    }

    /** The "Trend" action on the overview card: the Trend tab already shows the project-level trend. */
    public showOverviewTrend() {
        this.selectedGuiTabIndex = ProjectOverviewComponent.TREND_TAB_INDEX;
    }

    public mainTabClick() {
        this.loadMainTab();
    }

    public tabClick(tIx: number) {
        this.loadTab(tIx);
    }
    
    private createXlsxDownLoadLink() {
        //project-level export, available with or without forecast adjustments
        let link = `${this.dataService.getBaseUrl()}/Processing/DownloadXlsx?projectId=${this.projectId}`;
        this.xlsxDownloadLink = link;
    }

    public async deleteProject() {
        await this.projectService.deleteProject(this.projectId);
        this.router.navigateByUrl('/projects');
    }

    public showTrendDialog(cardIx: number, tabIx: number | undefined) {
        // For the forecast walk there are no existing tabs to navigate to,
        // so always open the modal
        let dialogParams = {
            projectId: this.projectId,
            viewId: undefined,
            tabId: undefined,
            cardIx: cardIx,
            tabIx: tabIx
        };
        
        const dialogRef = this.dialog.open(TrendModalComponent, {
            data: dialogParams,
            width: '90vw',
            maxWidth: '1400px',
            height: '90vh',
        });
        
        dialogRef.afterClosed().subscribe(result => {
            console.log('Trend dialog closed', result);
            if (result && result.shouldReload) {
                this.loadDataAsync(this.projectId);
            } else if (result && result.shouldExplode) {
                this.explodeCard(result.cardIx, tabIx);
            }
        });
    }

    public async duplicateView(view: CardsModel) {
    }

    //"Duplicate" of the Project overview node: a new view with the project's single Outcome card as a fresh start. The cards page shows a
    //duplicated view right away, so this page hands the new views over to it and opens the new view there
    public async duplicateProjectOverview() {
        try {
            this.loading = true;
            const views = await this.dataService.post<ViewsModel>('processing/duplicateProjectOverview', {
                projectId: this.projectId,
            });
            this.cardsLoadingService.updateViewsModel(views);
            const newView = views.views.find(v => v.isSelected);
            this.router.navigate(['/cards'], { queryParams: { projectId: this.projectId, tabuid: newView?.tabUId } });
        } finally {
            this.loading = false;
        }
    }

    public deleteView(view: CardsModel) {
    }

    public onDeleteFcstNode(node: FcstWalkTreeNode): void {
        const selectedTab = this.mainFcstWalkModel.selectedTabIx !== undefined
            ? this.mainFcstWalkModel.tabs[this.mainFcstWalkModel.selectedTabIx]
            : undefined;
        const selectionAffected = selectedTab &&
            selectedTab.path.length >= node.tab.path.length &&
            node.tab.path.every((v, i) => v === selectedTab.path[i]);

        const tabIx = this.mainFcstWalkModel.tabs.indexOf(node.tab);
        if (tabIx >= 0) {
            this.closeTab(tabIx);
        }

        if (selectionAffected) {
            const parentPath = node.tab.path.slice(0, -1);
            if (parentPath.length === 0) {
                this.mainTabClick();
            } else {
                const parentTabIx = this.mainFcstWalkModel.tabs.findIndex(t =>
                    t.path.length === parentPath.length &&
                    t.path.every((v, i) => v === parentPath[i])
                );
                this.tabClick(parentTabIx >= 0 ? parentTabIx : 0);
            }
        }
    }

    private handleForecastWalkNavigation(fcstWalk?: string): void {
        if (!fcstWalk || !this.mainFcstWalkModel) {
            return;
        }

        // Navigate to the appropriate forecast walk tab
        if (fcstWalk === 'main') {
            this.selectMainFcstWalkTab();
        } else {
            const tabIndex = parseInt(fcstWalk, 10);
            if (!isNaN(tabIndex)) {
                this.selectFcstWalkTab(tabIndex);
            }
        }
    }

    private selectMainFcstWalkTab() {
        if (!this.mainFcstWalkModel) {
            return;
        }
        
        this.mainFcstWalkModel.selectedTabIx = undefined;
        // Update the service with the modified model
        this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
        
        this.varianceModel = this.mainFcstWalkModel.varianceModel;
        this.loadWaterfallItems(this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome, this.varianceModel.cards, null, [], this.varianceModel.outcomeAfterOutOfScope);
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
        this.createXlsxDownLoadLink();
        this.selectedTabIx = undefined;
        this.selectedGuiTabIndex = this.mainFcstWalkModel.selectedGuiTabIndex !== undefined ? this.mainFcstWalkModel.selectedGuiTabIndex : 0;
        
        if (this.selectedGuiTabIndex === ProjectOverviewComponent.TREND_TAB_INDEX && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    private selectFcstWalkTab(tabIndex: number) {
        if (!this.mainFcstWalkModel || !this.mainFcstWalkModel.tabs || tabIndex < 0 || tabIndex >= this.mainFcstWalkModel.tabs.length) {
            return;
        }
        
        const tab = this.mainFcstWalkModel.tabs[tabIndex];
        this.mainFcstWalkModel.selectedTabIx = tabIndex;
        // Update the service with the modified model
        this.cardsLoadingService.updateMainFcstWalkModel(this.mainFcstWalkModel);
        
        this.varianceModel = tab.varianceModel;
        this.loadWaterfallItems(this.varianceModel.oldTotalOutcome, this.varianceModel.newTotalOutcome, this.varianceModel.cards, undefined, [], this.varianceModel.outcomeAfterOutOfScope);
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
        this.createXlsxDownLoadLink();
        this.selectedTabIx = tabIndex;
        this.selectedGuiTabIndex = tab.selectedGuiTabIndex !== undefined ? tab.selectedGuiTabIndex : 0;
        
        if (this.selectedGuiTabIndex === ProjectOverviewComponent.TREND_TAB_INDEX && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    public getSelectedTab(): FcstWalkTab | undefined {
        if (this.selectedTabIx === undefined || this.selectedTabIx === null) {
            return undefined;
        }
        return this.mainFcstWalkModel?.tabs?.[this.selectedTabIx];
    }

    public onTabChanged(index: number): void {
        this.selectedGuiTabIndex = index;

        if (!this.showFcst) {
            // VAR side: saved on the overview, not on the forecast walk (selectedTabIx may still point at the last viewed forecast tab)
            if (this.viewsModel?.overview) {
                this.viewsModel.overview.selectedGuiTabIndex = index;
            }
        } else if (this.mainFcstWalkModel) {
            if (this.selectedTabIx !== undefined && this.selectedTabIx !== null) {
                const selectedTab = this.getSelectedTab();
                if (selectedTab) {
                    selectedTab.selectedGuiTabIndex = index;
                }
            } else {
                this.mainFcstWalkModel.selectedGuiTabIndex = index;
            }
        }
        
        // If Trend tab is selected and hasn't been loaded yet, load it
        const trendTabIndex = ProjectOverviewComponent.TREND_TAB_INDEX;
        if (index === trendTabIndex && !this.getBenchmarksModel()) {
            this.loadBenchmarksModelData();
        }
    }

    public getBenchmarksModel(): BenchmarksModel | undefined {
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            return selectedTab.benchmarksModel;
        }
        return this.mainFcstWalkModel ? this.mainFcstWalkModel.benchmarksModel : this.rootBenchmarksModel;
    }
    
    public onFormCompChanged(formCompIx: number): void {
        this.loadBenchmarksModelData(formCompIx);
    }

    public async onFcstSaved(event: { formCompIx: number }): Promise<void> {
        // Reload the model to reflect forecast adjustments
        try {
            this.loading = true;
            const currentGuiTabIndex = this.selectedGuiTabIndex;
            const currentTabIx = this.selectedTabIx;

            // Clear all benchmarksModel caches in viewsModel (except the current one being edited is in mainFcstWalkModel)
            if (this.viewsModel?.views) {
                for (const view of this.viewsModel.views) {
                    view.benchmarksModel = undefined;
                    if (view.tabs) {
                        for (const tab of view.tabs) {
                            tab.benchmarksModel = undefined;
                        }
                    }
                }
            }

            // Clear all benchmarksModel caches in mainFcstWalkModel except the one being edited
            if (this.mainFcstWalkModel) {
                if (currentTabIx !== undefined) {
                    // A tab is selected, clear the main benchmarksModel
                    this.mainFcstWalkModel.benchmarksModel = undefined;
                }
                if (this.mainFcstWalkModel.tabs) {
                    for (let i = 0; i < this.mainFcstWalkModel.tabs.length; i++) {
                        if (i !== currentTabIx) {
                            this.mainFcstWalkModel.tabs[i].benchmarksModel = undefined;
                        }
                    }
                }
            }

            const mainFcstWalkRes = await this.cardsLoadingService.loadMainFcstWalkModel(this.projectId);
            if (mainFcstWalkRes && this.viewsModel) {
                this.viewsModel.haveForecastAdjustmentsDone = true;    //the first adjustment was just saved from the Trend tab: show the VAR/FOR switch
            }
            this.loadModel(mainFcstWalkRes, this.selectedTabIx?.toString());
            this.loadBenchmarksModelData(event.formCompIx);
            this.selectedGuiTabIndex = currentGuiTabIndex;
        } finally {
            this.loading = false;
        }
    }

    private async loadBenchmarksModelData(formCompIx?: number): Promise<void> {
        try {
            console.log('ProjectOverviewComponent.loadBenchmarksModelData');
            const selectedTab = this.getSelectedTab();

            // Only cache for URL without formCompIx
            if (formCompIx === undefined && this.getBenchmarksModel()) {
                console.log('loadBenchmarksModelData: Using cached default data');
                return;
            }

            let url: string;
            if (selectedTab) {
                // For a selected tab, we need to get the card index from the tab's path
                // The last element in the path is the card index in the parent tab
                const cardIndex = selectedTab.path[selectedTab.path.length - 1];
                const tabIx = this.selectedTabIx;
                url = `benchmarksForMainFcstWalk?projectId=${this.projectId}&tabIx=${tabIx}&cardIndex=${cardIndex}`;
            } else {
                url = `benchmarksForMainFcstWalk?projectId=${this.projectId}`;
            }
            if (formCompIx !== undefined) {
                url += `&formCompIx=${formCompIx}`;
            }
            const res = await this.dataService.get<BenchmarksModel>(url);
            
            if (selectedTab) {
                selectedTab.benchmarksModel = res;
            } else if (this.mainFcstWalkModel) {
                this.mainFcstWalkModel.benchmarksModel = res;
            } else {
                this.rootBenchmarksModel = res;    // no forecast adjustments yet: project-level trend is cached on the component
            }
            
        } catch (error) {
            console.error('Error loading BenchmarksModel for Trend tab:', error);
        }
    }

}